/**
 * Pure rules of the website product configurator (no React), shared by
 * src/components/product/product-configurator.tsx and the server-side
 * configuration model (configurator-model.ts) used by the reseller API.
 *
 * Moved verbatim out of the configurator so the API and the storefront use
 * literally the same option normalisation, delivery default, printing-method
 * preference and catalogue value rules. Change them here, for both.
 */
import { SUPPLIER_BOOKKEEPING_OPTION_IDS } from '@/lib/catalog-option-visibility';
import { isForeignSupplierValue } from '@/lib/printcom/foreign-supplier-values';
import { sanitizeOptInAddonDefault } from '@/lib/pricing/opt-in-addon-defaults';

export interface ProductOption {
  id: string;
  name: string;
  label: string;
  type: 'select' | 'radio' | 'checkbox' | 'number' | 'custom-size' | 'specification' | 'color' | 'swatch' | 'text' | 'textarea';
  required: boolean;
  locked?: boolean;
  hidden?: boolean;
  alwaysShow?: boolean;
  /**
   * Explicitly controls how values with images are presented. Material choices
   * can use full cards, while technical choices such as finish or thickness
   * stay compact. This is deliberately data-driven: translated labels are not
   * reliable enough to infer whether an option represents a material or size.
   */
  visualPresentation?: 'cards' | 'compact';
  showValueDescriptions?: boolean;
  propertyGroup?: string;
  showIf?: {
    optionId?: string;
    value?: string | string[];
    [key: string]: string | string[] | undefined;
  };
  values?: Array<{
    id: string;
    label: string;
    value: string;
    image?: string;
    overviewImage?: string;
    color?: string;
    visualSvgContent?: string;
    width?: number;
    height?: number;
    showIf?: {
      optionId?: string;
      value?: string | string[];
      [key: string]: string | string[] | undefined;
    };
    priceModifier?: number;
    areaPriceModifier?: number;
    metadata?: Record<string, any>;
    description?: string;
    eco?: boolean;
    type?: string;
    nullable?: boolean;
    customSizes?: {
      minWidth: number;
      maxWidth: number;
      minHeight: number;
      maxHeight: number;
    };
    template?: string;
    templateFileName?: string;
    templateDownload?: string;
    templateDownloadFileName?: string;
  }>;
  min?: number;
  max?: number;
  placeholder?: string;
  maxLength?: number;
  default?: string | number;
}

/**
 * Een optie waarvan elke waarde een interne artikelcode is (Print.com
 * `stock_item`: E1115BC, E1115CR ...) is leveranciersadministratie, geen keuze
 * voor een klant. Codes: letters, minstens drie cijfers, geen spaties.
 */
const ARTICLE_CODE = /^[A-Z]{0,4}\d{3,}[A-Z0-9-]*$/i;
export function isArticleCodeOnlyOption(option: { id?: string; values?: Array<{ label?: unknown; value?: unknown }> }): boolean {
  if (!option || option.id === 'copies' || !Array.isArray(option.values)) return false;
  // Alle varianten van artikelnummers (article_number, article_number_2 ...).
  if (String(option.id || '').startsWith('article_number')) return true;
  if (option.values.length < 2) return false;
  return option.values.every((value) => {
    const label = String(value?.label ?? value?.value ?? '').trim();
    return ARTICLE_CODE.test(label) && /[A-Z]/i.test(label);
  });
}

export const HIDDEN_CONSUMER_OPTION_IDS = new Set([
  ...SUPPLIER_BOOKKEEPING_OPTION_IDS,
  'printing',
  'printingmethod',
  'printingMethod',
  'white_print',
  'printing_side',
  'clean_cut',
  'packaging_extras',
  'brezo_article',
  'stock_item',
  'article_number_2',
  'supplier_shipment',
  'packaging_quantity_box',
  'data',
  'weight',
  'handling_costs',
  'product_weight',
  'summary_image',
  'numberOfDesigns',
  'designs',
  'customer_packing_option',
  'flexibleprintingmethod',
  'supplier_include',
  'personalize',
  'block-out-foil',
  'number_of_contour_lines',
  'box_delivery',
  'delivery',
  'pallet_delivery',
  'sealed',
  'bundle',
  'standard_bundle',
  'cross_bundle',
  'sheet_size',
  'article_number',
  'packaging_quantity',
  'product_size',
  'brand_name',
  'certificate',
  'fitting',
  'individually_sealed',
]);

export function isDeliverySpeedOption(option: ProductOption) {
  const key = `${option.id || ''} ${option.name || ''} ${option.label || ''}`.toLowerCase();
  if (/(urgency|levertijd|delivery[_\s-]?speed|production[_\s-]?time)/.test(key)) return true;

  const values = option.values?.map((value) => String(value.value || value.id || value.label || '').toLowerCase()) || [];
  const deliveryValues = ['standard', 'moderate', 'quick', 'urgent', 'fast', 'express', 'earlier', 'faster', 'spoed', 'snel'];
  return values.length > 0 && values.every((value) => deliveryValues.includes(value));
}

/** De normale productievariant is de route zonder toeslag bij Print.com. */
export function getCheapestDeliveryOptionValue(option: ProductOption): string | number | undefined {
  if (!isDeliverySpeedOption(option)) return undefined;
  const standard = option.values?.find((value) =>
    String(value.value ?? value.id ?? '').toLowerCase() === 'standard' && (value as any).hidden !== true
  );
  return standard?.value;
}

export function embeddedOptionImageFromDescription(value: any): string | null {
  const source = String(
    value?.description ||
      value?.metadata?.printcomDescription ||
      value?.metadata?.description ||
      value?.metadata?.materialDescription ||
      ''
  );
  const match = source.match(/!\[[^\]]*\]\(((?:https?:)?\/\/[^)]+)\)/i);
  if (!match?.[1]) return null;
  return match[1].startsWith('//') ? `https:${match[1]}` : match[1];
}

export function stripMarkdownImages<T>(value: T): T {
  if (typeof value !== 'string') return value;
  return value
    .replace(/!\[[^\]]*\]\((?:https?:)?\/\/[^)]+\)/gi, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s*(?:Lees meer|Read more|Mehr erfahren|En savoir plus)\b[\s\S]*$/i, '')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/[_*]{1,2}/g, '')
    .replace(/\s+([.,;:!?])/g, '$1')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim() as T;
}

export function isPrintedLetterheadsProduct(productKey?: string) {
  const normalized = String(productKey || '').toLowerCase();
  return normalized === 'printed-letterheads' || normalized === 'huisstijl-briefpapier';
}

export function normalizeConsumerOptions(productOptions: ProductOption[], locale: string, productSpecifications?: any): ProductOption[] {
  const lang = locale.split('-')[0];
  const noneLabels: Record<string, Record<string, string>> = {
    finish: {
      nl: 'Geen beschermlaag',
      de: 'Keine Schutzschicht',
      en: 'No protective layer',
      fr: 'Sans couche de protection',
    },
    drillholes: {
      nl: 'Geen boorgaten',
      de: 'Keine Bohrlöcher',
      en: 'No drill holes',
      fr: 'Sans trous de perçage',
    },
    rounded_corners: {
      nl: 'Geen afgeronde hoeken',
      de: 'Keine abgerundeten Ecken',
      en: 'No rounded corners',
      fr: 'Sans coins arrondis',
    },
  };
  const optionalNoneDefaults = new Set(['drillholes', 'rounded_corners']);
  const optionsWithNone = new Set(['finish', 'drillholes', 'rounded_corners']);
  const noneValueAliases: Record<string, string[]> = {
    finish: ['none', 'geen', 'no_laminate', 'no_lamination', 'without_lamination'],
    drillholes: ['none', 'geen', 'no_drillholes'],
    rounded_corners: ['none', 'geen', 'no_rounded_corners'],
  };

  return productOptions.map((option) => {
    if (!option.values) return option;

    const normalizedValues = option.values.map((value: any) => {
      const embeddedImage = embeddedOptionImageFromDescription(value);
      return {
        ...value,
        image: value.image || embeddedImage || undefined,
        description: stripMarkdownImages(value.description),
        metadata: value.metadata
          ? {
              ...value.metadata,
              description: stripMarkdownImages(value.metadata.description),
              printcomDescription: stripMarkdownImages(value.metadata.printcomDescription),
              materialDescription: stripMarkdownImages(value.metadata.materialDescription),
            }
          : value.metadata,
      };
    });
    const catalogAllowedValues = getPrintComCatalogAllowedValueSet(productSpecifications, option.id);
    const catalogFilteredValues = catalogAllowedValues
      ? normalizedValues.filter((value: any) => catalogAllowedValues.has(String(value.value).toLowerCase()) || isForeignSupplierValue(value))
      : normalizedValues;
    const catalogDefault = productSpecifications?.defaultOptions?.[option.id];
    const rawDefault = catalogDefault !== undefined && catalogDefault !== null && catalogDefault !== ''
      ? catalogDefault
      : option.default;

    const normalizedOption: ProductOption = {
      ...option,
      // Betaalde afwerkingen uit de leveranciersvoorbeeldconfiguratie (op vorm
      // snijden, nummering, boorgaten) zijn nooit de startkeuze.
      default: sanitizeOptInAddonDefault(option.id, rawDefault, [{ ...option, values: catalogFilteredValues }] as any),
      values: catalogFilteredValues,
    };

    if (!optionsWithNone.has(option.id)) return normalizedOption;
    if ((option as any).metadata?.suppressNoneOption === true || (option as any).allowNone === false) return normalizedOption;

    const label = noneLabels[option.id]?.[lang] || noneLabels[option.id]?.nl || 'Geen';
    const aliases = noneValueAliases[option.id] || ['none'];
    const allowedPrintcomValues = productSpecifications?.source === 'printcom' && productSpecifications?.fullPriceProperties?.[option.id]
      ? normalizeFullPricePropertyValues(productSpecifications.fullPriceProperties[option.id])
      : null;
    const hasNone = catalogFilteredValues.some((value) => {
      const optionValue = String(value.value).toLowerCase();
      const optionLabel = String(value.label || '').toLowerCase();
      return aliases.includes(optionValue) || optionLabel === 'geen';
    });
    const canUseNoneValue = allowedPrintcomValues
      ? aliases.some((alias) => allowedPrintcomValues.includes(alias))
      : option.id !== 'finish' || hasNone;
    if (!canUseNoneValue) return normalizedOption;
    let relabeledExistingNone = false;
    const values = hasNone
      ? catalogFilteredValues.map((value) => {
          const optionValue = String(value.value).toLowerCase();
          const optionLabel = String(value.label || '').toLowerCase();
          const isNoneValue = aliases.includes(optionValue) || optionLabel === 'geen';

          if (!isNoneValue || relabeledExistingNone) return value;
          relabeledExistingNone = true;
          return { ...value, label };
        })
      : [
          {
            id: `${option.id}-none`,
            label,
            value: 'none',
          },
          ...catalogFilteredValues,
        ];

    return {
      ...normalizedOption,
      required: false,
      default: optionalNoneDefaults.has(option.id) ? 'none' : normalizedOption.default,
      values,
    };
  });
}

export function mergeShowIfValues(showIf: any, controllerId: string, allowedValues: string[]) {
  const allowed = Array.from(new Set(allowedValues.map(String)));
  if (!showIf) return { [controllerId]: allowed };

  const normalized = showIf.optionId && showIf.value !== undefined
    ? { [showIf.optionId]: Array.isArray(showIf.value) ? showIf.value.map(String) : [String(showIf.value)] }
    : { ...showIf };

  const existing = normalized[controllerId];
  if (existing !== undefined) {
    const existingValues = Array.isArray(existing) ? existing.map(String) : [String(existing)];
    normalized[controllerId] = existingValues.filter((value) => allowed.includes(value));
  } else {
    normalized[controllerId] = allowed;
  }

  return normalized;
}

export function applyPrintComExcludeShowIfRules(
  productOptions: ProductOption[],
  productSpecifications?: any
) {
  const excludes = Array.isArray(productSpecifications?.excludes) ? productSpecifications.excludes : [];
  if (excludes.length === 0) return productOptions;

  const optionsById = new Map(productOptions.map((option) => [option.id, option]));
  const restrictions = new Map<string, Record<string, string[]>>();

  const addRestriction = (
    targetOptionId: string,
    targetValue: string,
    controllerId: string,
    allowedValues: string[]
  ) => {
    if (allowedValues.length === 0) return;
    const key = `${targetOptionId}::${targetValue}`;
    const existing = restrictions.get(key) || {};
    const current = existing[controllerId];
    existing[controllerId] = current
      ? current.filter((value) => allowedValues.includes(value))
      : allowedValues;
    restrictions.set(key, existing);
  };

  excludes.forEach((rule: any) => {
    if (!Array.isArray(rule) || rule.length < 2) return;

    rule.forEach((targetPart: any) => {
      const targetOptionId = String(targetPart?.property || '');
      if (targetOptionId === 'material' || targetOptionId === 'cover_material') return;
      const targetOption = optionsById.get(targetOptionId);
      const targetValues = Array.isArray(targetPart?.options) ? targetPart.options.map(String) : [];
      if (!targetOption || !targetOption.values?.length || targetValues.length === 0) return;

      rule.forEach((controllerPart: any) => {
        const controllerId = String(controllerPart?.property || '');
        if (!controllerId || controllerId === targetOptionId) return;
        const controllerOption = optionsById.get(controllerId);
        const blockedControllerValues = new Set(
          Array.isArray(controllerPart?.options) ? controllerPart.options.map(String) : []
        );
        if (!controllerOption || controllerOption.hidden || !controllerOption.values?.length || blockedControllerValues.size === 0) return;

        const allControllerValues = Array.from(new Set(
          controllerOption.values
            .map((value) => String(value.value ?? ''))
            .filter((value) => value && value !== 'null')
        ));
        const allowedControllerValues = allControllerValues.filter((value) => !blockedControllerValues.has(value));
        if (allowedControllerValues.length === 0 || allowedControllerValues.length === allControllerValues.length) return;

        targetValues.forEach((targetValue: string) => {
          addRestriction(targetOptionId, targetValue, controllerId, allowedControllerValues);
        });
      });
    });
  });

  if (restrictions.size === 0) return productOptions;

  return productOptions.map((option) => {
    if (!option.values?.length) return option;
    return {
      ...option,
      values: option.values.map((value) => {
        const restriction = restrictions.get(`${option.id}::${String(value.value)}`);
        if (!restriction) return value;

        const nextShowIf = Object.entries(restriction).reduce(
          (showIf, [controllerId, allowedValues]) => mergeShowIfValues(showIf, controllerId, allowedValues),
          value.showIf
        );

        return { ...value, showIf: nextShowIf };
      }),
    };
  });
}

export function applyProductSpecificOptionFixes(
  productOptions: ProductOption[],
  productKey?: string,
  _productSpecifications?: any
) {
  // A panel-with-frame has three-part rules such as material + size + brush
  // direction. The Print.com compatibility helper intentionally turns rules
  // into pairwise showIf restrictions, which would hide the 100 x 150 format
  // even while a non-brushed material is selected. Keep the original options
  // here; the configurator's normal exclude matcher below evaluates all rule
  // conditions together.
  // Deze afleiding stond eerst aan voor elk product behalve twee uitzonderingen.
  // Ze vertaalt uitsluitregels naar showIf-voorwaarden per paar opties, en zo'n
  // paarsgewijze vertaling is grover dan de regel zelf: een regel met drie
  // voorwaarden werd zo al afgedwongen zodra er twee klopten. Waarden
  // verdwenen daardoor uit de lijst terwijl de combinatie gewoon bestond.
  //
  // Erger nog bij twee opties die elkaar wederzijds uitsluiten: dan sluit hij
  // altijd een van beide kanten af, afhankelijk van waar de klant begint. Op
  // hpl-trespa kon je daardoor alleen onbedrukt bestellen, terwijl bedrukt
  // gewoon leverbaar is.
  //
  // De gewone uitsluitmatcher verderop beoordeelt alle voorwaarden van een
  // regel samen en kent bovendien de botsingsoplosser, die de tegenoptie laat
  // meebewegen in plaats van de keuze te verbergen. Deze afleiding voegt daar
  // niets aan toe en haalt er alleen keuzes vanaf, dus hij is uit.
  const optionsWithExcludeRules = productOptions;
  void applyPrintComExcludeShowIfRules;
  if (!isPrintedLetterheadsProduct(productKey)) return optionsWithExcludeRules;

  return optionsWithExcludeRules.map((option) => {
    if (option.id !== 'perforation' || !option.values?.length) return option;

    return {
      ...option,
      required: false,
      default: 'none',
      values: option.values.map((value) =>
        String(value.value) === 'none'
          ? { ...value, nullable: false }
          : value
      ),
    };
  });
}

export function applyProductSpecificSpecificationFixes(
  specifications: any,
  productKey?: string
) {
  const normalized = { ...(specifications || {}) };
  // Verified doming workfile: ordered size is trim; bleed and safety are
  // separate physical requirements, identical in every language bucket.
  if (productKey === 'doming-stickers') {
    normalized.bleed = 2.5;
    normalized.safeMarginMm = 1.5;
  }

  if (
    normalized.source === 'printcom' &&
    normalized.printComPricingCoverage?.complete === true &&
    normalized.defaultOptions &&
    typeof normalized.defaultOptions === 'object'
  ) {
    const localPricing = normalized.localPrintComPricing || {};
    const catalogKeys = new Set<string>(Object.keys(normalized.defaultOptions || {}));
    const collectKeys = (options: any) => {
      if (!options || typeof options !== 'object' || Array.isArray(options)) return;
      Object.keys(options).forEach((key) => catalogKeys.add(key));
    };
    if (Array.isArray(localPricing.rows)) {
      localPricing.rows.forEach((row: any) => collectKeys(row?.requestOptions));
    }
    collectKeys(localPricing.baselineOptions);
    collectKeys(localPricing.baseline?.requestOptions);

    const catalogFullPriceProperties: Record<string, string[]> = {};
    catalogKeys.forEach((key) => {
      const allowedValues = getPrintComCatalogAllowedValueSet(normalized, key);
      if (allowedValues && allowedValues.size > 0) {
        catalogFullPriceProperties[key] = Array.from(allowedValues);
      }
    });

    normalized.autoSetOptions = {
      ...(normalized.autoSetOptions || {}),
      ...(normalized.defaultOptions || {}),
    };
    normalized.fullPriceProperties = {
      ...(normalized.fullPriceProperties || {}),
      ...catalogFullPriceProperties,
    };
  }

  if (!isPrintedLetterheadsProduct(productKey)) {
    return normalized;
  }

  if (normalized.fullPriceProperties && typeof normalized.fullPriceProperties === 'object' && !Array.isArray(normalized.fullPriceProperties)) {
    const currentPerforationValues = normalizeFullPricePropertyValues(normalized.fullPriceProperties.perforation);
    normalized.fullPriceProperties = {
      ...normalized.fullPriceProperties,
      perforation: Array.from(new Set(['none', ...currentPerforationValues])),
    };
  }

  return normalized;
}

export function normalizeFullPricePropertyValues(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).toLowerCase());
  }
  if (value === null || value === undefined || value === '') {
    return [];
  }
  return [String(value).toLowerCase()];
}

export function getPrintComCatalogAllowedValueSet(specifications: any, optionId: string): Set<string> | null {
  if (
    specifications?.source !== 'printcom' ||
    specifications?.printComPricingCoverage?.complete !== true
  ) {
    return null;
  }

  if (specifications?.printComPricingCoverage?.configurationScope === 'validated-on-demand') {
    const supplierValues = normalizeFullPricePropertyValues(
      specifications?.supplierVisibleProperties?.[optionId]
    );
    return supplierValues.length ? new Set(supplierValues) : null;
  }

  const values = new Set<string>();
  const addOptionValue = (options: any) => {
    const value = options?.[optionId];
    if (value !== undefined && value !== null && value !== '') {
      values.add(String(value).toLowerCase());
    }
  };

  const localPricing = specifications.localPrintComPricing || {};
  if (Array.isArray(localPricing.rows)) {
    localPricing.rows.forEach((row: any) => addOptionValue(row?.requestOptions));
  }
  addOptionValue(localPricing.baselineOptions);
  addOptionValue(localPricing.baseline?.requestOptions);
  addOptionValue(specifications.defaultOptions);

  return values.size > 0 ? values : null;
}

export function getPreferredPrintComPrintingMethod(
  specifications: any,
  options: ProductOption[] = [],
  configuration: Record<string, string | number> = {}
) {
  if (specifications?.source !== 'printcom') return null;

  const printingMethodOption = options.find(
    (option) => option.id === 'printingmethod' || option.name === 'printingmethod'
  );
  if (!printingMethodOption?.values?.length) return null;

  const configuredMethod = String(
    configuration.printingmethod ||
    configuration.printing_method ||
    configuration.printmethod ||
    specifications?.defaultOptions?.printingmethod ||
    ''
  );
  if (
    configuredMethod &&
    printingMethodOption.values.some((value) =>
      String(value.value) === configuredMethod && matchesShowIf(value.showIf, configuration)
    )
  ) {
    return null;
  }

  const offsetValue = printingMethodOption.values.find((value) =>
    String(value.value) === 'offset' && matchesShowIf(value.showIf, configuration)
  );
  if (offsetValue) return 'offset';

  return null;
}

export function getForcedProductOptions(
  productKey: string | undefined,
  specifications?: any,
  options: ProductOption[] = [],
  configuration: Record<string, string | number> = {}
) {
  const preferredPrintingMethod = getPreferredPrintComPrintingMethod(specifications, options, configuration);
  if (preferredPrintingMethod) {
    return { printingmethod: preferredPrintingMethod };
  }
  return {};
}

export function matchesShowIf(showIf: any, config: Record<string, string | number>) {
  if (!showIf) return true;
  // Some stored option maps carry one redundant array wrapper (e.g. zitzak).
  // Only unwrap that proven representation; multi-clause array semantics are
  // not established. Preserve an empty condition list as no restriction.
  if (Array.isArray(showIf)) {
    if (showIf.length === 0) return true;
    if (showIf.length !== 1 || !showIf[0] || typeof showIf[0] !== 'object' || Array.isArray(showIf[0])) return false;
    return matchesShowIf(showIf[0], config);
  }
  const { optionId, value } = showIf;
  if (optionId && value !== undefined) {
    const selected = config[optionId];
    return Array.isArray(value) ? value.includes(String(selected)) : selected === value;
  }

  return Object.entries(showIf).every(([key, expected]) => {
    const selected = config[key];
    return Array.isArray(expected) ? expected.includes(String(selected)) : selected === expected;
  });
}

export const PRINT_TYPE_OPTION = /^(printtype|print_type|printcolou?rs?|colou?rs|print_colou?rs?)$/i;

export const NO_PRINT_VALUE = /^(geen|none|no print|zonder( print)?|unbedruckt|kein(e|en)? ?(druck)?|aucune?( impression)?|blanco|onbedrukt)$/i;

export function isNoPrintValue(optionId: string, value: { value?: unknown; label?: string }) {
  if (!PRINT_TYPE_OPTION.test(optionId)) return false;
  const raw = String(value.value ?? '');
  return raw === '00' || raw === '0/0' || NO_PRINT_VALUE.test(String(value.label || '').trim());
}

/** Waarden van een optie met de "geen print"-waarde achteraan, verder in de oorspronkelijke volgorde. */
export function orderPrintBeforeNone<T extends { value?: unknown; label?: string }>(option: { id: string; values?: T[] }) {
  const values = option.values || [];
  return [...values.filter((value) => !isNoPrintValue(option.id, value)), ...values.filter((value) => isNoPrintValue(option.id, value))];
}

/**
 * Visitekaartjes: veilige startwaarden. Leverancierscatalogi zetten soms een
 * betaalde afwerking of doos als technische default; die mag nooit de start
 * van een klantconfiguratie zijn.
 */
export const BUSINESS_CARD_SAFE_DEFAULTS: Record<string, string> = {
  fold: 'no_fold',
  finish: 'geen',
  spot_finish: 'none',
  spot_finish_back: 'none',
  delivery: 'default_businesscard',
  personalize: 'none',
  variable_creasing_line: 'none',
  die_cut: 'no',
  perforation: 'none',
  businesscard_creasing: 'no',
  folded: 'no',
  rounded_corners: 'none',
  drillholes: 'none',
  printingmethod: 'digital',
  clean_cut: 'yes',
  plano_delivery: 'yes',
  sheet_size: 'indigo_320w_460h',
  printtype: '44',
  bundle: 'bundle_per_50',
  size: 'bc',
};

export const BUSINESS_CARD_UNSAFE_AUTO_DEFAULTS: Record<string, Set<string>> = {
  fold: new Set(['single_4', 'single_6', 'single_8', 'single_10']),
  spot_finish: new Set(['foil_gold', 'foil_silver', 'spot_uv']),
  spot_finish_back: new Set(['foil_gold', 'foil_silver', 'spot_uv']),
  delivery: new Set(['businesscard_box']),
  personalize: new Set(['numbering', 'personalization']),
  variable_creasing_line: new Set(['1', '2', '3']),
  die_cut: new Set(['yes']),
  perforation: new Set(['1_perforation_line', '2_perforation_lines']),
  businesscard_creasing: new Set(['yes']),
};

export const businessCardAutoRepairProtectedOptionIds = new Set([
  'fold',
  'finish',
  'spot_finish',
  'spot_finish_back',
  'delivery',
  'personalize',
  'variable_creasing_line',
  'die_cut',
  'perforation',
  'businesscard_creasing',
  'folded',
  'rounded_corners',
  'drillholes',
]);

/** Vult lege of onveilige visitekaartjeswaarden met de veilige standaard. */
export function applyBusinessCardSafeDefaultsTo(config: Record<string, string | number>) {
  const next = { ...config };
  Object.entries(BUSINESS_CARD_SAFE_DEFAULTS).forEach(([key, safeValue]) => {
    const currentValue = next[key];
    const unsafeValues = BUSINESS_CARD_UNSAFE_AUTO_DEFAULTS[key];
    if (
      currentValue === undefined ||
      currentValue === '' ||
      currentValue === null ||
      (unsafeValues && unsafeValues.has(String(currentValue)))
    ) {
      next[key] = safeValue;
    }
  });
  return next;
}

/**
 * Sommige ingelezen catalogi bevatten zowel de echte Print.com-textielkleur
 * (color_textile) als een oude, verkorte `color`-alias met textile_*-waarden.
 * Die alias is geen tweede klantkeuze en veroorzaakt twee keer "Kleur" in de
 * configurator. Verberg hem uitsluitend wanneer de volledige tegenhanger ook
 * aanwezig is; een aparte kleur voor opdruk of borduurgaren blijft zichtbaar.
 */
export function isRedundantTextileColorAlias(option: ProductOption, availableOptions: ProductOption[]) {
  if (!/^colou?r$/i.test(option.id) || !option.values?.length) return false;
  const isTextileAlias = option.values.every((value) => /^textile_/i.test(String(value.value || value.id || '')));
  if (!isTextileAlias) return false;

  return availableOptions.some((candidate) =>
    candidate.id !== option.id
    && /^colou?r_textile$/i.test(candidate.id)
    && Boolean(candidate.values?.length),
  );
}

export function normalizeCustomShapeProviderValue(value: string | number | undefined) {
  const raw = String(value || '');
  if (!raw.includes('|shape:')) return raw;
  return raw.split('|shape:')[0] || raw;
}

export function printfulSizeRank(value: any) {
  const raw = String(value?.label || value?.value || '').toUpperCase().trim();
  const normalized = raw.replace(/[^A-Z0-9.]/g, '');
  const apparelOrder: Record<string, number> = {
    XXS: 10,
    XS: 20,
    S: 30,
    M: 40,
    L: 50,
    XL: 60,
    XXL: 70,
    XXXL: 80,
  };
  if (apparelOrder[normalized] !== undefined) return apparelOrder[normalized];

  const numericXl = normalized.match(/^([2-9])XL$/);
  if (numericXl) return 50 + Number(numericXl[1]) * 10;

  const numeric = raw.match(/(\d+(?:[.,]\d+)?)/);
  if (numeric) return Number(numeric[1].replace(',', '.'));

  return 999;
}

export function sortPrintfulSizeValues(values: any[]) {
  return [...values].sort((a, b) => printfulSizeRank(a) - printfulSizeRank(b));
}
